import { useState, useEffect, useMemo } from 'react';
import { Button, Form, Row, Col } from 'react-bootstrap';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';

import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Checkbox from '@mui/material/Checkbox';
import DeleteIcon from '@mui/icons-material/Delete';
import Box from '@mui/material/Box';

import { useRestApi } from 'context/RestApiContext';
import { useMaster } from 'context/MasterContext';
import { MessageModal } from 'components/modal/MessageModal';

import TextComponent from 'components/Input/TextComponent';
import SelectComponent from 'components/Input/SelectComponent';

// 業務タスク
function TaskCardAddModal(props) {
  const master = useMaster();
  const restApi = useRestApi();

  const [show, setShow] = useState(false);

  // タスク情報
  const [task, setTask] = useState({});
  // チェックリスト
  const [checklist, setChecklist] = useState([]);
  const [newItem, setNewItem] = useState('');
  // チェックリスト項目の候補（必要に応じて差し替え）
  const checklistSuggestions = [];

  // 既に入力済み（チェックリストに存在する）項目は候補から除外
  const filteredChecklistSuggestions = useMemo(() => {
    const taken = new Set(checklist.map((it) => (it.text ?? '').trim().toLowerCase()));
    return checklistSuggestions.filter((s) => !taken.has((s ?? '').trim().toLowerCase()));
  }, [checklist]);

  // 確認ダイアログ
  const [dialog, setDialog] = useState({});

  // モード
  const [mode, setMode] = useState('');

  // 編集処理
  const handleOnChange = (prop) => (target) => {
    setTask({ ...task, [prop]: target?.value ?? target?.target.value });
  };

  useEffect(() => {
    if (props?.params?.show) {
      setTask({
        TITLE: '',
        DESCRIPTION: '',
        LIST_ID: props.params.LIST_ID,
      });
      setChecklist([]); // チェックリスト初期化
      setShow(true);
    }
  }, [props.params]);

  // 保存ボタンのクリック処理
  const handleOnClickSave = () => {
    let expTime = task.EXPIRY_TIME ? ` ${task.EXPIRY_TIME}` : '';
    let exp = task.EXPIRY_DATE ? task.EXPIRY_DATE + expTime : null;

    let _task = {
      ...task,
      EXPIRY: exp,
      checklist: checklist.map((it, i) => ({
        WORK_CONTENT: it.text,
        DONE_FLG: it.checked ? 1 : 0,
        POSITION: i + 1,
      })),
    };

    restApi.post('/api/task/cards/add', _task, (res) => {
      props.params.callBack();
      setShow(false);
    });
  };

  // 閉じるボタンのクリック処理
  const handleClose = () => {
    props.loadCards('task');
    setShow(false);
  };

  // チェックリスト操作
  const handleAddChecklistItem = (textArg) => {
    const text = ((textArg ?? newItem) || '').trim();
    if (!text) return;
    setChecklist((prev) => [
      ...prev,
      { id: `${Date.now()}-${Math.random()}`, text, checked: false },
    ]);

    // エンターキーで追加した場合に残ってしまうので時間差で、入力欄をクリア
    setTimeout(() => {
      setNewItem('');
    }, 10);
  };

  // チェックリスト項目のチェック/アンチェック切替
  const handleToggleChecklistItem = (id) => {
    setChecklist((prev) => prev.map((it) => (it.id === id ? { ...it, checked: !it.checked } : it)));
  };

  // チェックリスト項目のテキスト変更
  const handleChangeChecklistItemText = (id) => (e) => {
    const value = e?.target?.value ?? '';
    setChecklist((prev) => prev.map((it) => (it.id === id ? { ...it, text: value } : it)));
  };

  // チェックリスト項目の削除
  const handleDeleteChecklistItem = (id) => {
    setChecklist((prev) => prev.filter((it) => it.id !== id));
  };

  return (
    <>
      <Dialog
        sx={{
          '& .MuiDialog-container': {
            alignItems: 'flex-start',
            '& .MuiPaper-root': {
              width: '100%',
              maxWidth: '1120px',
              backgroundColor: '#f1f2f4',
              marginTop: props?.params?.mode == 'add' ? '20px' : '60px',
            },
          },
        }}
        open={show}
        disableEnforceFocus={true} // フォーカス制御を無効にする
      >
        <DialogTitle className="pl-3">
          {props?.params?.mode != 'upd' ? (
            <Form.Control
              type="text"
              placeholder="カード名を入力してください"
              value={task.TITLE}
              onChange={handleOnChange('TITLE')}
              style={{ width: '600px' }}
              autoFocus
            />
          ) : (
            <>{task.TITLE}</>
          )}
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent className="pl-1 pb-0">
          <Row className="m-0">
            <Col md="6">
              <div className="description-label">
                <i className="material-symbols-rounded">date_range</i>
                <b>期限</b>
              </div>
            </Col>
          </Row>
          <Row className="m-0">
            <Col md="2">
              <TextComponent
                type="date"
                value={task.EXPIRY_DATE}
                onChange={handleOnChange('EXPIRY_DATE')}
              ></TextComponent>
            </Col>
            <Col md="2" className="p-0 pl-1">
              <SelectComponent value={task.EXPIRY_TIME} onChange={handleOnChange('EXPIRY_TIME')}>
                <option value={''}>{'　'}</option>
                {Array.from({ length: 11 }, (_, i) => i + 8)
                  .map((i) => `${i.toString().padStart(2, '0')}:00`)
                  .map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
              </SelectComponent>{' '}
            </Col>
          </Row>
          {/* 説明 */}
          <Row className="m-0 mt-2">
            <Col md="6">
              <div className="description-label">
                <i className="material-symbols-rounded">format_align_left</i>
                <b>説明</b>
              </div>
            </Col>
          </Row>
          <Row className="m-0">
            <Col md="12">
              <Form.Control
                as="textarea"
                placeholder="説明を入力してください。"
                rows={5}
                value={task.DESCRIPTION || ''} // 修正: 入力値を反映
                onChange={handleOnChange('DESCRIPTION')}
              />
            </Col>
          </Row>
          {/* チェックリスト */}
          <Row className="m-0 mt-3">
            <Col md="6">
              <div className="description-label">
                <i className="material-symbols-rounded">playlist_add_check</i>
                <b>チェックリスト</b>
              </div>
            </Col>
          </Row>
          <Row className="m-0">
            <Col md="9" className="p-0 m-0">
              <List dense sx={{ m: 0, p: 0.25 }}>
                {checklist.map((it) => (
                  <ListItem
                    key={it.id}
                    sx={{
                      bgcolor: 'inherit', // 親に合わせる
                      borderRadius: 1,
                      mb: 0,
                      mt: 0,
                    }}
                    secondaryAction={
                      <IconButton
                        edge="end"
                        aria-label="delete"
                        onClick={() => handleDeleteChecklistItem(it.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    }
                  >
                    <Checkbox
                      edge="start"
                      checked={!!it.checked}
                      onChange={() => handleToggleChecklistItem(it.id)}
                      tabIndex={-1}
                    />
                    <Box sx={{ ml: 1, flex: 1 }}>
                      <TextField
                        fullWidth
                        size="small"
                        value={it.text}
                        onChange={handleChangeChecklistItemText(it.id)}
                        sx={{
                          '& .MuiOutlinedInput-root': { backgroundColor: '#fff' },
                          '& .MuiOutlinedInput-input': {
                            textDecoration: it.checked ? 'line-through' : 'none',
                            color: it.checked ? 'text.disabled' : 'inherit',
                          },
                        }}
                      />
                    </Box>
                  </ListItem>
                ))}
              </List>
            </Col>
          </Row>
          <Row className="mt-1 m-0">
            <Col md="9">
              <Autocomplete
                freeSolo
                options={filteredChecklistSuggestions}
                inputValue={newItem}
                onInputChange={(_, v) => setNewItem(v ?? '')}
                // onChange は削除（選択しただけで追加されないようにする）
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="項目を入力して Enter または 追加 を押下"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
                        e.preventDefault();
                        handleAddChecklistItem();
                      }
                    }}
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': { backgroundColor: '#fff' },
                    }}
                  />
                )}
              />
            </Col>
            <Col md="1" className="pl-1">
              <Button
                style={{ height: '40px', paddingTop: '6px' }}
                disabled={!newItem}
                variant="primary"
                className="btn-fill"
                onClick={() => handleAddChecklistItem()}
              >
                追加
              </Button>
            </Col>
          </Row>
        </DialogContent>
        <DialogActions className="p-3" style={{ justifyContent: 'space-between' }}>
          <Button variant="secondary" className="footer-button" onClick={handleClose}>
            閉じる
          </Button>
          <Button
            variant="primary"
            className="btn-fill  ml-4 mr-2"
            onClick={handleOnClickSave}
            disabled={!task.TITLE}
            style={{ flexGrow: 1 }}
          >
            保存
          </Button>
        </DialogActions>
      </Dialog>
      {/* メッセージダイアログ */}
      <MessageModal dialog={dialog}></MessageModal>
    </>
  );
}

export default TaskCardAddModal;
